// Load polyfills (Object.groupBy, Array.prototype.at, String.prototype.replaceAll) before the route tree
// evaluates, since router.tsx is the client entry and routeTree.gen pulls in every route module.
import './polyfills'
import type { ReactNode } from 'react'
import { isServer } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { routeTree } from './routeTree.gen'
import { makeQueryClient, queryClient as clientQueryClient, persistOptions } from './main'
import { useAppStore } from './lib/app-store'
import { createLocaleI18n, loadTranslation } from './lib/locales'
import { resolveServerLocale } from './lib/server-locale'
import type { Locale } from 'shared/locales'
import { setupAnalytics } from './lib/analytics'
import { DefaultRouteError } from './route-error'
import { logFrontendError } from './lib/log-error'
import { addBreadcrumb, installBreadcrumbCapture } from './lib/breadcrumbs'
import { installClientActionCapture, trackNavigation } from './lib/session-actions'
import { setupVersionDriftReload, suppressTanstackChunkReload } from './lib/app-version'

/**
 * Router factory consumed by TanStack Start. The app is full-SSR: loaders run on the server and deferred
 * prefetches stream their result. That is load-bearing for the report routes — the server-rendered HTML is what
 * paints the report on reload (the persisted-cache restore is async and racy), so they must NOT be
 * `ssr: 'data-only'`. The query client must be per-request on the server; a module singleton would leak one
 * request's dehydrated cache into the next on the long-lived server.
 */
export async function getRouter() {
  const queryClient = isServer ? makeQueryClient() : clientQueryClient

  // Resolve the request's locale ONCE, here, so SSR and the first client render agree — and load +
  // activate its catalog BEFORE the router is created and React renders/hydrates.
  const locale: Locale = isServer ? (resolveServerLocale() ?? 'es-ES') : useAppStore.getState().store.locale

  // Request-scoped Lingui instance: on the server this isolates each request's active locale from concurrent
  // renders sharing the worker (React #418 hydration mismatches). See createLocaleI18n.
  const i18nInstance = createLocaleI18n()
  await loadTranslation(locale, i18nInstance)

  const router = createRouter({
    routeTree,
    // Loaders prefetch into this client via `context.queryClient`; the ssr-query
    // integration below dehydrates the same instance and streams it to the client.
    context: { queryClient, locale, i18n: i18nInstance },
    defaultPreload: 'intent',
    // Loader calls must never be stale, so the loader runs whenever a route is preloaded or visited.
    defaultPreloadStaleTime: 0,
    // Blank default: every route OWNS its pending UI. This just wraps routes WITHOUT a skeleton in a Suspense
    // boundary that renders nothing, so their data/chunk suspense is caught in place instead of blanking the app.
    defaultPendingComponent: () => null,
    // A suspense query throws during render, so without this an auth/test error on a streamed query crashes the whole
    // tree. Pass the request-scoped i18n: the boundary renders ABOVE AppShell's provider and must not localize
    // against a concurrent request's locale (React #418).
    defaultErrorComponent: (props) => <DefaultRouteError {...props} i18n={i18nInstance} />,
    Wrap: (props: { children: ReactNode }) => {
      return (
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          {props.children}
        </PersistQueryClientProvider>
      )
    },
  })

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  })

  // Google Analytics (client + prod only). Consent-gated: GA loads and tracks only after the visitor
  // opts in to statistics cookies, and reacts to later changes. See lib/analytics.ts + cookie-consent.ts.
  setupAnalytics(router)

  if (!isServer) {
    // A dynamic import that cannot be fetched fires `vite:preloadError`. Do NOT reload: hashed assets persist on the
    // CDN, so a failed import is almost always the network, and reloading into a dead network blanks the page.
    // Suppress TanStack's own automatic reload too, so the rejection reaches the route error boundary.
    window.addEventListener('vite:preloadError', (event) => {
      suppressTanstackChunkReload((event as Event & { payload?: unknown }).payload)
    })

    // A new build deployed mid-session keeps the client on stale code. Reload on the next navigation — or sooner, if
    // it crashes first on a payload the new build reshaped (#88, handled in route-error). See lib/app-version.
    setupVersionDriftReload(router)

    // Breadcrumbs are in-memory and cost nothing until a crash; the session timeline POSTs batches, so it only runs
    // when the wide-events sink is enabled.
    const sessionTimelineOn = window.WIDE_EVENTS_ENABLED === 'true'
    router.subscribe('onResolved', (event) => {
      addBreadcrumb('nav', event.toLocation.href)
      if (sessionTimelineOn) trackNavigation(event.toLocation.href)
    })
    installBreadcrumbCapture()
    if (sessionTimelineOn) installClientActionCapture()

    // Errors that escape React's error boundaries — async callbacks, event handlers, setTimeout, etc.
    // — never reach `defaultErrorComponent`, so report them here too.
    window.addEventListener('error', (event) => {
      // Cross-origin script errors (GA, third-party embeds) surface with `event.error == null` and no
      // actionable detail — skip them.
      if (event.error == null) return
      logFrontendError(event.error, 'global')
    })

    window.addEventListener('unhandledrejection', (event) => {
      logFrontendError(event.reason, 'unhandledrejection')
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>
  }
}
